import React, { useEffect, useState } from "react";
import { HeadSEO } from "@/components/SEO/HeadSEO";
import * as StyledLayout from "@/layouts/Styled.Layout";
import * as StyledTypography from "@/styles/Styled.Typography";
import * as StyledButtons from "@/styles/Styled.Buttons";
import * as StyledResources from "./Styled.Resources";
import LogoSerif from "./Logos/LogoSerif";
import LogoNotion from "./Logos/LogoNotion";
import LogoGumroad from "./Logos/LogoGumroad";
import LogoGrammarly from "./Logos/LogoGrammarly";
import LogoHosting from "./Logos/LogoHosting";

const resourceSection = [
  {
    title: "Business Tools",
    items: [
      {
        title: "Affinity by Serif",
        description: (
          <>
            "One of the excellent alternatives for creatives who don’t wish to
            pay Adobe’s hefty subscription model. I used all three Serif
            products - Designer 2 (Illustrator), Photo 2 (Photoshop), and
            Publisher 2 (Indesign). Besides comparable features and options,
            which I’ve got used to, perhaps I’m favourably disposed towards the
            idea of the - paid once, and yours forever to keep - concept."
          </>
        ),
        link: "https://affinity.serif.com/en-us/affinity-pricing/",
        buttonLabel: "Try Affinity",
        logo: <LogoSerif />,
      },
      {
        title: "Gumroad",
        description: (
          <>
            "A superb online marketplace where you can list and sell almost
            anything except for some products associated with{" "}
            <StyledTypography.StyledLink
              as="a"
              href="https://gumroad.com/help/article/155-things-you-cant-sell-on-gumroad"
              target="_blank"
              rel="noopener noreferrer"
            >
              high risk
            </StyledTypography.StyledLink>
            . What I like about Gumroad is setting up is a breeze for beginners,
            and anyone can start selling in minutes. Listing your products is
            free. No need for monthly subscription fees. They only get a cut per
            sale of your products. This is a fair trade-off on both sides. You
            don't have to worry about paying extra monthly charges if you are
            already struggling with getting any sales.{" "}
            <StyledTypography.StyledLink
              as="a"
              href="https://stanleyyeo.gumroad.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              See how my products are listed
            </StyledTypography.StyledLink>{" "}
            on Gumroad when you are free"
          </>
        ),
        link: "https://discover.gumroad.com/?a=525915891",
        buttonLabel: "Try Gumroad",
        logo: <LogoGumroad />,
      },
      {
        title: "Notion",
        description: (
          <>
            "My workflow used to include multiple apps such as Pages, Notes, and
            Google Sheets, which make it inconveniently cumbersome to track and
            return to where I left off because of the numerous formats and
            versions. With Notion, I can easily pen down ideas, take notes, and
            write all in one workspace. Notion's pages/databases/templates are
            highly customisable. That allows me to build templates to help
            designers win (
            <StyledTypography.StyledLink
              as="a"
              href="https://stanleyyeo.gumroad.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Head over to my shop here
            </StyledTypography.StyledLink>
            ). Best of all? You can use all the robust features for free. Did I
            mention you can use Notion to collaborate with a team of people on
            documents, tasks, notes, and databases? Yes, you can. Consider the
            Plus Plan if you need more version histories, unlimited file
            uploads, and invitations to 100 collaborators. Otherwise, you will
            be great with just the Free plan."
          </>
        ),
        link: "https://affiliate.notion.so/o2bvoppduepc",
        buttonLabel: "Try Notion",
        logo: <LogoNotion />,
      },
      {
        title: "Grammarly",
        description: (
          <>
            "Can I let you in on a little secret? I use a proofreading tool.
            Because I suck at writing. For that reason, Grammarly has come to my
            rescue - purging out grammar, spelling, and punctuation mistakes in
            almost 90% of the words you read on this website so you can
            understand the mumbo jumbo I am writing (Okay, I know I still suck
            at writing). Grammarly has become my English teacher, transforming
            my Singlish into proper English. Apart from highlighting my
            mistakes, it showed me how to correct them. Seize the advantage of
            Grammarly Premium now with its generative AI - write faster and
            cleverer, and at the same time, elevate your writing with style,
            tone, and clarity. Before you get accused of copying, Grammarly’s
            plagiarism detection capabilities got you covered."
          </>
        ),
        link: "https://grammarly.go2cloud.org/SH3jG",
        buttonLabel: "Try Grammarly",
        logo: <LogoGrammarly />,
      },
      {
        title: "Hosting (Formerly A2hosting)",
        description: (
          <>
            "I have to admit A2 Hosting isn’t the cheapest out there.
            Considering a 99.9 uptime commitment, free SSL certificate,
            unlimited business emails, unlimited add-on domains, and Money-Back
            Guarantee, it is good enough for my small business. If your website
            requires top speed, its Turbo Plans claim to be 20x faster. What I
            like about A2 Hosting is their excellent customer service 24/7/365,
            which I can conveniently get in touch with via Phone, Live Chat or
            Email Support. That said, there are no free domains with any hosting
            plan sign-ups compared to other web hosting companies. But then,
            would you place all eggs in one basket?"
          </>
        ),
        link: "http://www.a2hosting.com/?aid=649c1168ba042&cid=edae5de3",
        buttonLabel: "Try Hosting",
        logo: <LogoHosting />,
      },
    ],
  },
];

export default function Resources({ resources = resourceSection }) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Stanley Yeo",
    url: "https://stanleyyeo.com",
  };

  const [colCount, setColCount] = useState(3); // default desktop

  // Update column count based on viewport width
  useEffect(() => {
    function updateColCount() {
      if (window.innerWidth < 1308) setColCount(1);
      else setColCount(2);
    }
    updateColCount();
    window.addEventListener("resize", updateColCount);
    return () => window.removeEventListener("resize", updateColCount);
  }, []);

  if (!resources || resources.length === 0) return null; // safety check

  return (
    <>
      <HeadSEO
        siteTitle="Resources"
        siteDescription="Browse curated tools, frameworks, and resources for web design, WordPress, and branding to support your next project."
        structuredData={structuredData}
      />

      <StyledTypography.ParadoxSubtitle as="h2" className="fade-up">
        The tools you need to break the rules.
      </StyledTypography.ParadoxSubtitle>
      <StyledLayout.Grid>
        <StyledLayout.GridItem>
          <p data-animate="fade-up">
            Please understand that I have experience with these products and
            services. I recommend them because they are helpful and useful, not
            because of the little commission I make if you decide to purchase.
          </p>
          <p data-animate="fade-up">
            Only buy if you feel you need them or they will help achieve your
            goals.
          </p>
          <StyledTypography.TypoCaption2 as="small" data-animate="fade-up">
            Disclosure: Recommended tools may contain affiliate links. This
            means if you decide to make a purchase through the links, I will
            earn a little commission at no additional expense to you. Read my
            disclosure for more info.
          </StyledTypography.TypoCaption2>
        </StyledLayout.GridItem>
      </StyledLayout.Grid>
      <StyledResources.Grid>
        {resources.map((section) => {
          const items = section.items;
          const remainder = items.length % colCount;
          const placeholders = remainder === 0 ? 0 : colCount - remainder;

          return (
            <React.Fragment key={section.title}>
              <StyledResources.GridItem>
                <StyledTypography.TypoSubtitle2 as="h3">
                  {section.title}
                </StyledTypography.TypoSubtitle2>
              </StyledResources.GridItem>
              <StyledResources.GridItem>
                <StyledResources.GridContentWrapper>
                  {section.items.map((item) => (
                    <StyledResources.GridItemContent key={item.link}>
                      <StyledResources.LogoLink as="a" href={item.link}>
                        {item.logo}
                      </StyledResources.LogoLink>
                      <StyledResources.VendorTitle>
                        {item.title}
                      </StyledResources.VendorTitle>
                      <p>{item.description}</p>
                      <StyledButtons.PrimaryButton as="button">
                        <a href={item.link}>{item.buttonLabel}</a>
                      </StyledButtons.PrimaryButton>
                    </StyledResources.GridItemContent>
                  ))}
                  {/* Invisible placeholders to fill row */}
                  {colCount > 1 &&
                    Array.from({ length: placeholders }).map((_, i) => (
                      <StyledResources.GridItemContent
                        $placeholder
                        key={`ph-${i}`}
                      />
                    ))}
                </StyledResources.GridContentWrapper>
              </StyledResources.GridItem>
            </React.Fragment>
          );
        })}
      </StyledResources.Grid>
    </>
  );
}
